import '@computerrock/react-app-polyfill/ie11';
import '@computerrock/react-app-polyfill/stable';
import React, {useState, useEffect, lazy, Suspense, useMemo} from 'react';
import * as ReactDOMClient from 'react-dom/client';
import PropTypes from 'prop-types';
import {FormationApp, configureStore, serviceWorker} from '@computerrock/formation-core';
import {createBrowserHistory, renderRoutes} from '@computerrock/formation-router';
import {FileAssetsProvider} from '@ace-de/eua-file-assets';
import './application/initDynamicEnv';
import config from './config';
import getRoutes from './routes';
import routePaths from './routePaths';
import modals from './modals';
import reducers from './reducers';
import sagas from './sagas';
import serviceManager from './serviceManager';
import MemberSearchProvider from './members/MemberSearchProvider';

// initialize history
const history = createBrowserHistory({basename: config.PUBLIC_URL});

// initialize store
const store = configureStore({
    routes: getRoutes(),
    history,
    reducers,
    serviceManager,
});
store.runSagas(sagas);

// load i18nService
const i18nService = serviceManager.loadService('i18nService');

// load arcGISMapService
const arcGISMapService = !config.IS_FALLBACK_SYSTEM ? serviceManager.loadService('arcGISMapService') : null;

// Application
const Application = ({store, history}) => {
    const [routes, setRoutes] = useState(getRoutes());

    const ArcGISMapProvider = useMemo(() => {
        if (config.IS_FALLBACK_SYSTEM) return;
        return lazy(() => (
            import('@ace-de/eua-arcgis-map').then(m => ({default: m.ArcGISMapProvider}))
        ));
    }, []);
    const GoogleMapProvider = useMemo(() => {
        if (!config.IS_FALLBACK_SYSTEM) return;
        return lazy(() => (
            import('@ace-de/eua-google-map').then(m => ({default: m.GoogleMapProvider}))
        ));
    }, []);

    useEffect(() => {
        return window.DYNAMIC_ENV.onEnvChange((property, value) => {
            if (property === 'IS_MAINTENANCE_MODE_ON') {
                setRoutes(getRoutes());
                console.log(`Dynamic ENV update: ${property}=${value}`); // eslint-disable-line no-console
            }
        });
    }, []);

    return (
        <FormationApp
            store={store}
            history={history}
            modals={modals}
            routes={routes}
            routePaths={routePaths}
            defaultRoutePath={routePaths.DASHBOARD}
            i18nService={i18nService}
            render={({children}) => (
                <FileAssetsProvider>
                    {config.IS_FALLBACK_SYSTEM
                        ? (
                            <Suspense fallback={children}>
                                <GoogleMapProvider
                                    apiKey={config.GOOGLE_PLACES_API_KEY}
                                    shouldWaitForExternalGoogleScript={true}
                                >
                                    <MemberSearchProvider searchResultsRoute={routePaths.MEMBER_SEARCH}>
                                        {children}
                                    </MemberSearchProvider>
                                </GoogleMapProvider>
                            </Suspense>
                        ) : (
                            <Suspense fallback={children}>
                                <ArcGISMapProvider service={arcGISMapService}>
                                    <MemberSearchProvider searchResultsRoute={routePaths.MEMBER_SEARCH}>
                                        {children}
                                    </MemberSearchProvider>
                                </ArcGISMapProvider>
                            </Suspense>
                        )}
                </FileAssetsProvider>
            )}
        >
            {renderRoutes(routes)}
        </FormationApp>
    );
};

Application.propTypes = {
    store: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
};

// render application
const root = ReactDOMClient.createRoot(document.getElementById('root'));
root.render(<Application store={store} history={history} />);

// webpack hot module replacement for ./routes configuration,
// ./saga configuration and state is not hot reloaded but needs to
// be accepted so HMR can work properly
if (module.hot) {
    module.hot.accept(['./routes', './sagas'], () => {
        root.render(<Application store={store} history={history} />);
    });
}

// webpack hot module replacement for reducers
if (module.hot) {
    module.hot.accept('./reducers', () => {
        const nextReducers = require('./reducers').default;
        store.hotReloadReducers(nextReducers);
    });
}

// register service worker
serviceWorker.register({
    // reload on new content
    onUpdate: registration => {
        const waitingServiceWorker = registration.waiting;

        if (waitingServiceWorker) {
            waitingServiceWorker.addEventListener('statechange', event => {
                if (event.target.state === 'activated') {
                    window.location.reload();
                }
            });
            waitingServiceWorker.postMessage({type: 'SKIP_WAITING'});
        }
    },
});
